<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("SUPPLIERS.SUPPLIERS_LIST") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openSupplierCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_SUPPLIERS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{
                $t("SUPPLIERS.ADD_SUPPLIER")
              }}</span>
            </base-button>
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_SUPPLIERS)"
              :objectType="'suppliers'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <supplier-list-table
        @onViewSupplier="openSupplierViewModal"
        @onEditSupplier="openSupplierEditModal"
        @onDeleteSupplier="deleteSupplier"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewSupplierModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewSupplierModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'SUPPLIER_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("SUPPLIERS.VIEW_SUPPLIER") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openSupplier"
                :objectType="'suppliers'"
                :objectId="openSupplier.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="edit"
                  @click="openSupplierEditModal(openSupplier)"
                >
                  <i class="fal fa-edit"></i>
                  <span>{{ $t("COMMON.EDIT") }}</span>
                </button>
                <button class="delete" @click="deleteSupplier(openSupplier)">
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeSupplierModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-supplier-component
              v-if="openSupplier"
              :supplierId="openSupplier.id"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditSupplierModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditSupplierModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'SUPPLIER'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("SUPPLIERS.EDIT_SUPPLIER") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="view"
                  @click="openSupplierViewModal(openSupplier)"
                >
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeSupplierModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-supplier-component
              v-if="openSupplier"
              :supplierId="openSupplier.id"
              @onViewSupplier="openSupplierViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddSupplierModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddSupplierModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'SUPPLIER'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("SUPPLIERS.ADD_SUPPLIER") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeSupplierModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-supplier-component @onViewSupplier="openSupplierViewModal" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import SupplierListTable from "./partials/SupplierListTable.vue";
import EditSupplierComponent from "./components/EditSupplierComponent.vue";
import AddSupplierComponent from "./components/AddSupplierComponent.vue";
import ViewSupplierComponent from "./components/ViewSupplierComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    SupplierListTable,
    NotificationSubscription,
    EditSupplierComponent,
    AddSupplierComponent,
    ViewSupplierComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const locationId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewSupplierModalOpened = false;
    let isEditSupplierModalOpened = false;
    let isAddSupplierModalOpened = false;
    let openSupplier = null;
    if (locationId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewSupplierModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditSupplierModalOpened = true;
      }
      openSupplier = { id: locationId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddSupplierModalOpened = true;
    }
    return {
      isViewSupplierModalOpened: isViewSupplierModalOpened,
      isEditSupplierModalOpened: isEditSupplierModalOpened,
      isAddSupplierModalOpened: isAddSupplierModalOpened,
      openSupplier: openSupplier,
      renderKey: 1,
    };
  },

  methods: {
    openSupplierCreateModal() {
      this.closeSupplierModal();
      this.isAddSupplierModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Suppliers",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openSupplierViewModal(supplier, reRender = false) {
      this.closeSupplierModal();
      this.openSupplier = supplier;
      this.isViewSupplierModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Suppliers",
          query: { id: this.openSupplier.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openSupplierEditModal(supplier) {
      this.closeSupplierModal();
      this.openSupplier = supplier;
      this.isEditSupplierModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Suppliers",
          query: { id: this.openSupplier.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeSupplierModal() {
      this.isAddSupplierModalOpened = false;
      this.isViewSupplierModalOpened = false;
      this.isEditSupplierModalOpened = false;
      this.openSupplier = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Suppliers",
          query: {},
        }).href
      );
    },

    async deleteSupplier(supplier) {
      const confirmation = await swal.fire({
        title: this.$t("SUPPLIERS.DELETE_THIS_SUPPLIER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("suppliers/destroy", supplier.id);
          this.renderKey++;
          this.closeSupplierModal();
          this.$notify({
            type: "success",
            message: this.$t("SUPPLIERS.SUPPLIER_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
